import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { MapPinned, Maximize2, Minimize2, X } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import {
  hasValidSignalCoordinates,
  SIGNAL_CLASSIFICATION_META,
  SIGNAL_NO_DATA_CLASSIFICATION,
  SIGNAL_CLASSIFICATION_ORDER,
} from '../utils/sinalCtoAnalytics';
import { normalizeNumber, normalizeText } from '../utils/ocupacaoRedeAnalytics';

const DEFAULT_CENTER = [-3.5, -54.8];
const DEFAULT_ZOOM = 7;
const CLUSTER_GROUP_PROPS = {
  chunkedLoading: true,
  chunkInterval: 120,
  chunkDelay: 40,
  animate: false,
  showCoverageOnHover: false,
  spiderfyOnMaxZoom: true,
  zoomToBoundsOnClick: true,
  removeOutsideVisibleBounds: true,
  maxClusterRadius: 48,
};
const panelClassName =
  'rounded-[24px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_16px_40px_rgba(15,23,42,0.10)]';
const EXPANDED_MAP_TITLE_ID = 'sinal-cto-map-expanded-title';

const rxFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getSignalColor = (classification) => SIGNAL_CLASSIFICATION_META[classification]?.color || '#64748b';

const buildSignalMarkerIcon = (cto) => {
  const color = getSignalColor(cto.classificacao_sinal);
  const size = Math.max(14, Math.min(24, 14 + normalizeNumber(cto.qtd_clientes)));

  return L.divIcon({
    html: `
      <span
        class="signal-cto-marker"
        style="
          display:block;
          width:${size}px;
          height:${size}px;
          border-radius:9999px;
          background:${color};
          border:2px solid rgba(255,255,255,0.82);
          box-shadow:0 8px 18px ${color}42;
        "
      ></span>
    `,
    className: 'signal-cto-marker-shell',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)],
    signalClassification: cto.classificacao_sinal,
  });
};

const buildPointsViewportSignature = (points) => {
  if (!Array.isArray(points) || points.length === 0) {
    return 'default';
  }

  if (points.length === 1) {
    const [lat, lng] = points[0];
    return `single:${Number(lat).toFixed(4)}:${Number(lng).toFixed(4)}`;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  points.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });

  return [
    points.length,
    minLat.toFixed(3),
    maxLat.toFixed(3),
    minLng.toFixed(3),
    maxLng.toFixed(3),
  ].join(':');
};

const buildClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 42 : count < 100 ? 50 : 58;
  const children = cluster.getAllChildMarkers();
  const worstIndex = children.reduce((acc, marker) => {
    const index = SIGNAL_CLASSIFICATION_ORDER.indexOf(marker?.options?.icon?.options?.signalClassification);
    return Math.max(acc, index);
  }, -1);
  const classification = worstIndex >= 0
    ? SIGNAL_CLASSIFICATION_ORDER[worstIndex]
    : SIGNAL_NO_DATA_CLASSIFICATION;
  const markerColor = getSignalColor(classification);

  return L.divIcon({
    html: `
      <div class="infra-cluster-shell" style="
        width:${size}px;
        height:${size}px;
        background:radial-gradient(circle at 30% 30%, ${markerColor} 0%, ${markerColor}dd 100%);
        box-shadow:0 10px 24px ${markerColor}30;
      ">
        <span>${count}</span>
      </div>
    `,
    className: 'infra-cluster-icon',
    iconSize: [size, size],
  });
};

const FitMapToBounds = ({ points }) => {
  const map = useMap();
  const previousSignatureRef = useRef(null);

  useEffect(() => {
    const nextSignature = buildPointsViewportSignature(points);

    if (previousSignatureRef.current === nextSignature) {
      return;
    }

    previousSignatureRef.current = nextSignature;

    if (!Array.isArray(points) || points.length === 0) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
      return;
    }

    if (points.length === 1) {
      map.setView(points[0], 13, { animate: false });
      return;
    }

    map.fitBounds(points, {
      padding: [32, 32],
      animate: false,
      maxZoom: 14,
    });
  }, [map, points]);

  return null;
};

const RefreshMapSize = ({ refreshToken }) => {
  const map = useMap();

  useEffect(() => {
    let rafId = 0;
    let nestedRafId = 0;

    rafId = window.requestAnimationFrame(() => {
      nestedRafId = window.requestAnimationFrame(() => {
        map.invalidateSize({ pan: false, debounceMoveend: true });
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.cancelAnimationFrame(nestedRafId);
    };
  }, [map, refreshToken]);

  return null;
};

const MapViewport = ({
  markerPoints,
  markers,
  height = '500px',
  minHeight = '450px',
  fillHeight = false,
  refreshToken,
}) => (
  <div className={`overflow-hidden rounded-[22px] border border-[color:var(--border)] ${fillHeight ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      scrollWheelZoom
      preferCanvas
      zoomAnimation={false}
      fadeAnimation={false}
      markerZoomAnimation={false}
      className={`w-full ${fillHeight ? 'h-full flex-1' : ''}`}
      style={{ height, minHeight }}
    >
      <RefreshMapSize refreshToken={refreshToken} />
      <TileLayer
        attribution="© OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitMapToBounds points={markerPoints} />
      <MarkerClusterGroup {...CLUSTER_GROUP_PROPS} iconCreateFunction={buildClusterIcon}>
        {markers}
      </MarkerClusterGroup>
    </MapContainer>
  </div>
);

const SinalCtoMap = ({ rows = [], loading = false, isSyncing = false, onSelectCto = null }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const expandedPanelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const expandButtonRef = useRef(null);
  const wasExpandedRef = useRef(false);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (!isExpanded) {
      if (wasExpandedRef.current) {
        window.requestAnimationFrame(() => {
          expandButtonRef.current?.focus();
        });
      }

      wasExpandedRef.current = false;
      return undefined;
    }

    wasExpandedRef.current = true;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
        return;
      }

      if (event.key !== 'Tab' || !expandedPanelRef.current) {
        return;
      }

      const focusableElements = expandedPanelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (!firstFocusable || !lastFocusable) {
        event.preventDefault();
        return;
      }

      if (!expandedPanelRef.current.contains(document.activeElement)) {
        event.preventDefault();
        firstFocusable.focus();
        return;
      }

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded]);

  const validRows = useMemo(
    () => rows.filter((row) => hasValidSignalCoordinates(row)),
    [rows],
  );

  const markerPoints = useMemo(
    () => validRows.map((row) => [row.latitude, row.longitude]),
    [validRows],
  );

  const markers = useMemo(() => (
    validRows.map((cto) => {
      return (
        <Marker
          key={cto.id_cto}
          position={[cto.latitude, cto.longitude]}
          icon={buildSignalMarkerIcon(cto)}
        >
          <Popup>
            <div className="min-w-[240px] text-sm text-slate-800">
              <strong className="block text-base text-slate-900">{normalizeText(cto.caixa)}</strong>
              <div className="mt-2 space-y-1">
                <div>ID CTO: {cto.id_cto}</div>
                <div>Cidade: {normalizeText(cto.cidade)}</div>
                <div>POP: {normalizeText(cto.pop)}</div>
                <div>Projeto: {normalizeText(cto.projeto)}</div>
              </div>
              <hr className="my-3 border-slate-200" />
              <div className="space-y-1">
                <div>Média RX: {Number.isFinite(Number(cto.media_sinal_rx)) ? `${rxFormatter.format(Number(cto.media_sinal_rx))} dBm` : 'Não informado'}</div>
                <div>Média TX: {Number.isFinite(Number(cto.media_sinal_tx)) ? `${rxFormatter.format(Number(cto.media_sinal_tx))} dBm` : 'Não informado'}</div>
                <div>Clientes: {normalizeNumber(cto.qtd_clientes)}</div>
                <div>Classificação RX: {normalizeText(cto.classificacao_sinal)}</div>
                {cto.classificacao_divergente ? (
                  <div className="text-amber-700">Classificação divergente da faixa calculada</div>
                ) : null}
              </div>
              {onSelectCto ? (
                <button
                  type="button"
                  onClick={() => onSelectCto(cto)}
                  className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-blue-400 hover:text-blue-700"
                >
                  Ver clientes desta caixa
                </button>
              ) : null}
            </div>
          </Popup>
        </Marker>
      );
    })
  ), [onSelectCto, validRows]);

  return (
    <>
      {isExpanded ? (
        <div className="fixed inset-0 z-[119] bg-slate-950/78" onClick={() => setIsExpanded(false)} aria-hidden="true" />
      ) : null}

      <div
        ref={expandedPanelRef}
        role={isExpanded ? 'dialog' : undefined}
        aria-modal={isExpanded ? true : undefined}
        aria-labelledby={isExpanded ? EXPANDED_MAP_TITLE_ID : undefined}
        className={
          isExpanded
            ? 'fixed inset-x-4 top-[calc(var(--header-shell-height)+12px)] bottom-4 z-[120] flex flex-col overflow-hidden rounded-[28px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] p-4 shadow-[0_28px_80px_rgba(2,6,23,0.45)] sm:inset-x-8 sm:top-[calc(var(--header-shell-height)+16px)] sm:bottom-6 sm:p-5 lg:inset-x-12'
            : panelClassName
        }
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3
              id={isExpanded ? EXPANDED_MAP_TITLE_ID : undefined}
              className={`${isExpanded ? 'text-lg' : 'text-sm'} font-semibold text-[color:var(--text-primary)]`}
            >
              {isExpanded ? 'Distribuição de sinal por CTO' : 'Mapa geográfico de sinal'}
            </h3>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              CTOs com coordenadas válidas, clusterização e leitura por classificação de sinal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] px-3 py-1 text-[11px] text-[color:var(--text-muted)]">
              <MapPinned className="h-3.5 w-3.5" />
              OpenStreetMap
            </span>
            {isExpanded ? (
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setIsExpanded(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
                aria-label="Fechar mapa expandido"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        <div className={`mb-3 flex items-center ${isExpanded ? 'justify-between' : 'justify-end'}`}>
          {isExpanded ? (
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-muted)]">
              <MapPinned className="h-3.5 w-3.5 text-[color:var(--accent)]" />
              Mapa geográfico expandido
            </div>
          ) : null}
          <button
            ref={expandButtonRef}
            type="button"
            onClick={() => setIsExpanded((current) => !current)}
            className="inline-flex min-h-[38px] items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-3 py-1.5 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
            aria-label={isExpanded ? 'Minimizar mapa geográfico' : 'Maximizar mapa geográfico'}
          >
            {isExpanded ? (
              <>
                <Minimize2 className="h-3.5 w-3.5 text-[color:var(--accent)]" />
                Minimizar
              </>
            ) : (
              <>
                <Maximize2 className="h-3.5 w-3.5 text-[color:var(--accent)]" />
                Expandir mapa
              </>
            )}
          </button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2.5">
          {SIGNAL_CLASSIFICATION_ORDER.map((classification) => (
            <div
              key={classification}
              className="inline-flex items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-3 py-1.5 text-[11px] text-[color:var(--text-muted)]"
            >
              <span
                className="inline-flex h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: getSignalColor(classification) }}
                aria-hidden="true"
              />
              <span className="font-medium text-[color:var(--text-primary)]">{classification}</span>
            </div>
          ))}
        </div>

        {!loading && isSyncing ? (
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/72 px-3 py-1.5 text-[11px] text-[color:var(--text-muted)]">
            <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-[color:var(--accent)]" aria-hidden="true" />
            Mapa sincronizando com os filtros
          </div>
        ) : null}

        {loading ? (
          <div className="min-h-[450px] animate-pulse rounded-[22px] border border-[color:var(--border)] bg-[color:var(--surface-strong)]" />
        ) : null}

        {!loading && rows.length === 0 ? (
          <div className="flex min-h-[450px] items-center justify-center rounded-[22px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-6 text-center text-sm text-[color:var(--text-muted)]">
            Nenhuma CTO com sinal encontrada para os filtros selecionados.
          </div>
        ) : null}

        {!loading && rows.length > 0 && validRows.length === 0 ? (
          <div className="flex min-h-[450px] items-center justify-center rounded-[22px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-6 text-center text-sm text-[color:var(--text-muted)]">
            Os filtros retornaram registros de sinal, mas nenhuma CTO possui coordenadas válidas para exibição no mapa.
          </div>
        ) : null}

        {!loading && validRows.length > 0 ? (
          <div className={isExpanded ? 'flex min-h-0 flex-1 flex-col' : ''}>
            <MapViewport
              markerPoints={markerPoints}
              markers={markers}
              height={isExpanded ? '100%' : '500px'}
              minHeight={isExpanded ? '100%' : '450px'}
              fillHeight={isExpanded}
              refreshToken={isExpanded ? 'expanded' : 'compact'}
            />
          </div>
        ) : null}
      </div>
    </>
  );
};

export default memo(SinalCtoMap);
